import { useState, useRef, useEffect, useCallback } from 'react';
import { Mail, ArrowRight, Loader2, ShieldCheck, X, ChevronLeft, User, MapPin, Globe, Sparkles, Lock, Phone, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '@/App';
import LegalDocModal from '@/components/legal/LegalDocModal';

// ─────────────────────────────────────────────────────────────────────
// Post-signup behaviour — DO NOT MERGE THESE TWO FLOWS
//
// QuickOTPModal supports two signup entry points with intentionally
// different post-signup behaviour:
//
//   1. In-modal signup (gate triggers: Like / Apply / Contact / etc.)
//        → On success, the modal CLOSES and the original page is
//          revealed. The auth state updates globally so the parent
//          rerenders with `user` populated. NO redirect.
//        → The user stays in context — they were mid-action and the
//          page they were already on is the right destination.
//
//   2. Full `/auth` page signup (top-nav Sign In, deep links, etc.)
//        → On success, AuthPage REDIRECTS via the `?return=` query
//          param (falls back to role-based dashboard).
//        → The user came to /auth intentionally — they expect to
//          arrive somewhere new after auth completes.
//
// Inside the modal, the "Sign up free" button switches to step='signup'
// — it does NOT navigate to /auth?mode=signup. This is intentional.
// Mar 1, 2026 — Step 3 of homepage migration.
// ─────────────────────────────────────────────────────────────────────

const INTEREST_OPTIONS = [
  { id: 'admissions', label: 'Admissions', color: '#2F80ED' },
  { id: 'visa', label: 'Visa Services', color: '#7B61FF' },
  { id: 'insurance', label: 'Insurance', color: '#00B894' },
  { id: 'rpl', label: 'RPL Assessment', color: '#F39C12' },
  { id: 'skills', label: 'Skill Assessment', color: '#E74C3C' },
  { id: 'rentals', label: 'Accommodation', color: '#1ABC9C' },
];

const COUNTRIES = [
  'Australia', 'Canada', 'United Kingdom', 'United States', 'New Zealand',
  'Germany', 'Ireland', 'Singapore', 'India', 'Nepal', 'Philippines',
  'Bangladesh', 'Pakistan', 'Sri Lanka', 'China', 'Vietnam', 'Nigeria',
  'Kenya', 'South Africa', 'Brazil', 'Other',
];

export default function QuickOTPModal({ onSuccess, onClose, dismissable = true, guestMode = true, guestTitle = null, returnUrl = null }) {
  const { setUser } = useAuth();
  // Default to 'login' (password login) — returning users see this first
  const [step, setStep] = useState('login'); // 'login' | 'signup' | 'otp' | 'profile' | 'forgot' | 'forgot_otp' | 'forgot_reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showSignupPwd, setShowSignupPwd] = useState(false);
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [signupPassword, setSignupPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState(null);

  // Check for referral param — supports ?ref= query param AND /agents/:agentId URL path
  const getRefAgent = () => {
    const urlRef = new URLSearchParams(window.location.search).get('ref');
    if (urlRef) return urlRef;
    // Auto-detect agent ID from agent profile URL: /agents/:agentId
    const pathMatch = window.location.pathname.match(/^\/agents\/([^/]+)/);
    if (pathMatch) return pathMatch[1];
    return localStorage.getItem('ref_agent') || null;
  };
  const getRefCode = () => {
    const urlCode = new URLSearchParams(window.location.search).get('ref_code');
    if (urlCode) return urlCode;
    return localStorage.getItem('ref_code') || null;
  };
  const refAgent = getRefAgent();
  const refCode = getRefCode();
  // Persist it so it survives page navigation
  useEffect(() => {
    if (refAgent) localStorage.setItem('ref_agent', refAgent);
    if (refCode) localStorage.setItem('ref_code', refCode);
  }, [refAgent, refCode]);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef([]);
  // Legal consent (Terms + Privacy) — required before signup OTP send.
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // 'terms' | 'privacy' | null

  // Quick Profile state
  const [verifiedToken, setVerifiedToken] = useState(null);
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    interests: [],
    current_country: '',
    current_city: '',
  });

  // Resend cooldown timer
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleSendOTP = async (e) => {
    if (e) e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    // Signup gate — Terms + Privacy consent required.
    if (step === 'signup' && !legalAccepted) {
      toast.error('Please agree to our Terms and Conditions to continue.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/otp/send`, { email });
      if (step === 'forgot') {
        setStep('forgot_otp');
      } else {
        setStep('otp');
      }
      setResendTimer(30);
      if (res.data.dev_otp) {
        setDevOtp(res.data.dev_otp);
      }
      toast.success('OTP sent to your email!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    if (e) e.preventDefault();
    if (!email || !password) { toast.error('Enter email and password'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      const { token, user: userData } = res.data;
      localStorage.setItem('token', token);
      setUser(userData);
      toast.success(`Welcome back, ${userData.name}!`);
      onSuccess(userData, token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = useCallback((index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((d, i) => {
        if (index + i < 6) newOtp[index + i] = d;
      });
      setOtp(newOtp);
      const focusIdx = Math.min(index + digits.length, 5);
      inputRefs.current[focusIdx]?.focus();
      return;
    }
    const digit = value.replace(/\D/g, '');
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [otp]);

  const handleOTPKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }, [otp]);

  // Auto-verify when all 6 digits entered
  const otpString = otp.join('');
  useEffect(() => {
    if (otpString.length === 6) {
      if (step === 'forgot_otp') {
        handleForgotVerifyOTP(otpString);
      } else {
        verifyOTP(otpString);
      }
    }
  }, [otpString]);

  const verifyOTP = async (code) => {
    setLoading(true);
    try {
      const payload = { email, otp: code };
      // Pass password from signup form for new user registration
      if (signupPassword && signupPassword.length >= 4) payload.password = signupPassword;
      if (refAgent) payload.ref_agent = refAgent;
      if (refCode) payload.ref_code = refCode;
      const res = await axios.post(`${API}/auth/otp/verify`, payload);
      const { token, user, profile_complete, is_new_user } = res.data;
      localStorage.setItem('token', token);
      setVerifiedToken(token);
      setVerifiedUser(user);

      // ── Auto-record legal acceptance if user ticked the consent
      // checkbox on the signup form. Reuses the same POST /legal/me/accept
      // endpoint as the re-acceptance modal — single source of truth
      // for version tracking. Fire-and-forget; failure here MUST NOT
      // block signup. The user can still re-accept via the modal.
      if (legalAccepted) {
        try {
          await axios.post(`${API}/legal/me/accept`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (err) {
          // Non-blocking — they'll see the modal on first nav and can
          // accept there. We still log for observability.
          // eslint-disable-next-line no-console
          console.warn('[legal] auto-accept failed', err?.response?.status);
        }
      }

      // Process referral signup credits if ref_code was used
      if (refCode && !profile_complete) {
        try {
          await axios.post(`${API}/referrals/process-signup`, { new_user_id: user.user_id, referral_code: refCode });
        } catch {}
      }

      if (profile_complete) {
        // Existing user with complete profile — login immediately
        setUser(user);
        toast.success(`Welcome back, ${user.name}!`);
        localStorage.removeItem('ref_agent');
        localStorage.removeItem('ref_code');
        onSuccess(user, token);
      } else if (!is_new_user && user.name) {
        // Returning user who never completed Quick Profile but has a name — let them in
        setUser(user);
        toast.success(`Welcome back, ${user.name}!`);
        localStorage.removeItem('ref_agent');
        localStorage.removeItem('ref_code');
        onSuccess(user, token);
      } else {
        // New user or incomplete profile — show Quick Profile
        setProfileData(prev => ({ ...prev, name: user.name || '' }));
        setStep('profile');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const toggleInterest = (id) => {
    setProfileData(prev => ({
      ...prev,
      interests: prev.interests.includes(id)
        ? prev.interests.filter(i => i !== id)
        : [...prev.interests, id],
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileData.name.trim()) return toast.error('Please enter your name');
    if (!profileData.phone.trim()) return toast.error('Please enter your mobile number');
    if (!profileData.current_country) return toast.error('Please select your country');
    if (!profileData.current_city.trim()) return toast.error('Please enter your city');

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/quick-profile`, profileData);
      const updatedUser = res.data.user;
      setUser(updatedUser);
      toast.success(`Welcome, ${updatedUser.name}!`);
      onSuccess(updatedUser, verifiedToken);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotVerifyOTP = async (code) => {
    // After OTP verified for forgot password, go to set new password step
    setOtp(code.split(''));
    setStep('forgot_reset');
  };

  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, {
        email,
        otp: otp.join(''),
        new_password: newPassword,
      });
      const { token, user: userData } = res.data;
      localStorage.setItem('token', token);
      setUser(userData);
      toast.success('Password reset! You\'re logged in.');
      onSuccess(userData, token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Can close: only if dismissable AND not on profile step (profile is skippable with button)
  const canClose = dismissable && step !== 'profile';

  const stepTitle = guestMode && guestTitle && step === 'login' ? guestTitle : {
    login: 'Login',
    signup: 'Get Started',
    otp: 'Verify Email',
    profile: 'Quick Profile',
    forgot: 'Forgot Password',
    forgot_otp: 'Verify Email',
    forgot_reset: 'Set New Password',
  }[step] || 'Login';

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center" data-testid="quick-otp-modal">
      {/* Legal documents modal (nested on top of signup) — closes back
          to the signup form on X / I Understand without losing state. */}
      {legalModal && (
        <LegalDocModal
          documentType={legalModal}
          onClose={() => setLegalModal(null)}
          onUnderstand={() => setLegalModal(null)}
        />
      )}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={canClose ? onClose : undefined} />
      <div className="relative bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:w-[90%] sm:max-w-sm overflow-hidden animate-scale-in pb-safe">
        {/* Header */}
        <div className="flex items-center p-4 border-b border-slate-100">
          {['otp', 'signup', 'forgot', 'forgot_otp', 'forgot_reset'].includes(step) ? (
            <button onClick={() => {
              if (step === 'forgot_otp' || step === 'forgot_reset') { setStep('forgot'); setOtp(['','','','','','']); setNewPassword(''); }
              else if (step === 'otp') { setStep('signup'); setOtp(['','','','','','']); }
              else { setStep('login'); setPassword(''); setSignupPassword(''); }
            }} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors" data-testid="otp-back-btn">
              <ChevronLeft className="h-4 w-4 text-slate-600" />
            </button>
          ) : (
            <div className="w-8" />
          )}
          <h2 className="text-base font-bold text-[#0A2540] flex-1 text-center" data-testid="otp-modal-title">
            {stepTitle}
          </h2>
          {canClose ? (
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors" data-testid="otp-close-btn">
              <X className="h-4 w-4 text-slate-600" />
            </button>
          ) : (
            <div className="w-8" />
          )}
        </div>

        <div className="p-5">
          {step === 'login' ? (
            /* ===== DEFAULT: PASSWORD LOGIN ===== */
            <form onSubmit={handlePasswordLogin}>
              {guestMode ? (
                <>
                  {/* Unexa wordmark for guest mode — friendlier than a lock icon */}
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-200">
                    <span className="text-white font-black text-base tracking-tight">Unexa</span>
                  </div>
                  <p className="text-center text-sm text-slate-500 mb-5" data-testid="guest-subtitle">
                    Create a free account to continue
                  </p>
                  {/* Primary CTA for guests: Sign up first (large outlined button) */}
                  <button
                    type="button"
                    onClick={() => setStep('signup')}
                    className="w-full mb-3 py-3.5 rounded-2xl border-2 border-[#2F80ED] text-[#2F80ED] font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-50 active:scale-[0.98] transition-all"
                    data-testid="guest-signup-cta"
                  >
                    <Sparkles className="h-4 w-4" /> Sign up free
                  </button>
                  <div className="flex items-center gap-2 my-4">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">or</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                    <Lock className="h-7 w-7 text-[#2F80ED]" />
                  </div>
                  <p className="text-center text-sm text-slate-500 mb-5">
                    Login with your email and password.
                  </p>
                </>
              )}
              <div className="relative mb-3">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                  autoFocus
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="relative mb-2">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type={showLoginPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full pl-10 pr-11 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowLoginPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label={showLoginPwd ? 'Hide password' : 'Show password'}
                  data-testid="login-toggle-password"
                >
                  {showLoginPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="flex justify-end mb-4">
                <button type="button" onClick={() => setStep('forgot')}
                  className="text-[11px] text-[#2F80ED] font-semibold hover:underline"
                  data-testid="forgot-password-link">
                  Forgot Password?
                </button>
              </div>
              <button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full py-3.5 rounded-2xl bg-[#2F80ED] text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#1A6BD4] active:scale-[0.98] transition-all disabled:opacity-50 shadow-lg shadow-blue-200"
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRight className="h-4 w-4" /> Login</>}
              </button>
              <button type="button" onClick={() => setStep('signup')}
                className={`w-full mt-3 text-xs text-[#2F80ED] font-semibold hover:underline ${guestMode ? 'hidden' : ''}`}
                data-testid="switch-to-signup-btn">
                New here? Sign up with OTP
              </button>
              <AgentRegisterLink onClose={onClose} />
            </form>

          ) : step === 'signup' ? (
            /* ===== SIGNUP: EMAIL + PASSWORD + OTP ===== */
            <form onSubmit={handleSendOTP}>
              <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-7 w-7 text-[#2F80ED]" />
              </div>
              <p className="text-center text-sm text-slate-500 mb-5">
                Create your account. We'll send a verification code.
              </p>
              <div className="relative mb-3">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                  autoFocus
                  required
                  data-testid="signup-email-input"
                />
              </div>
              <div className="relative mb-4">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type={showSignupPwd ? 'text' : 'password'}
                  value={signupPassword}
                  onChange={e => setSignupPassword(e.target.value)}
                  placeholder="Create a password"
                  className="w-full pl-10 pr-11 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSignupPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label={showSignupPwd ? 'Hide password' : 'Show password'}
                  data-testid="signup-toggle-password"
                >
                  {showSignupPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <p className="text-[10px] text-slate-400 mt-1 ml-1">For faster login next time (min 4 chars)</p>
              </div>
              {/* Legal consent — required for signup. Linked Terms /
                  Privacy open the in-app LegalDocModal (no new tab). */}
              <label className="flex items-start gap-2 mb-3 cursor-pointer text-[11px] text-slate-500 leading-relaxed">
                <input
                  type="checkbox"
                  checked={legalAccepted}
                  onChange={e => setLegalAccepted(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-blue-600"
                  data-testid="signup-legal-checkbox"
                />
                <span>
                  I agree to the{' '}
                  <button type="button" className="text-[#2F80ED] font-semibold hover:underline" onClick={() => setLegalModal('terms')} data-testid="signup-terms-link">
                    Terms and Conditions
                  </button>{' '}
                  and{' '}
                  <button type="button" className="text-[#2F80ED] font-semibold hover:underline" onClick={() => setLegalModal('privacy')} data-testid="signup-privacy-link">
                    Privacy Policy
                  </button>
                  .
                </span>
              </label>
              <button
                type="submit"
                disabled={loading || !email || !legalAccepted}
                className="w-full py-3.5 rounded-2xl bg-[#2F80ED] text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#1A6BD4] active:scale-[0.98] transition-all disabled:opacity-50 shadow-lg shadow-blue-200"
                data-testid="signup-send-otp-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRight className="h-4 w-4" /> Send Verification Code</>}
              </button>
              <div className="flex items-center justify-center gap-1.5 mt-3">
                <ShieldCheck className="h-3 w-3 text-slate-300" />
                <p className="text-[10px] text-slate-400">Secure one-time code sent to your email</p>
              </div>
              <button type="button" onClick={() => setStep('login')}
                className="w-full mt-3 text-xs text-[#2F80ED] font-semibold hover:underline"
                data-testid="switch-to-login-btn">
                Already have an account? Login
              </button>
            </form>

          ) : step === 'forgot' ? (
            /* ===== FORGOT PASSWORD: ENTER EMAIL ===== */
            <form onSubmit={handleSendOTP}>
              <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
                <Lock className="h-7 w-7 text-amber-500" />
              </div>
              <p className="text-center text-sm text-slate-500 mb-5">
                Enter your email and we'll send a code to reset your password.
              </p>
              <div className="relative mb-4">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                  autoFocus
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-3.5 rounded-2xl bg-amber-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-600 active:scale-[0.98] transition-all disabled:opacity-50 shadow-lg shadow-amber-200"
                data-testid="forgot-send-otp-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRight className="h-4 w-4" /> Send Reset Code</>}
              </button>
            </form>

          ) : step === 'forgot_reset' ? (
            /* ===== SET NEW PASSWORD AFTER OTP ===== */
            <form onSubmit={handleResetPassword}>
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-7 w-7 text-emerald-500" />
              </div>
              <p className="text-center text-sm text-slate-500 mb-1">OTP verified for</p>
              <p className="text-center text-sm font-bold text-[#0A2540] mb-5">{email}</p>
              <div className="relative mb-4">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type={showResetPwd ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8, 1 letter + 1 number)"
                  className="w-full pl-10 pr-11 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                  autoFocus
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowResetPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label={showResetPwd ? 'Hide password' : 'Show password'}
                  data-testid="reset-toggle-password"
                >
                  {showResetPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <p className="text-[10px] text-slate-400 mt-1 ml-1">Minimum 4 characters</p>
              </div>
              <button
                type="submit"
                disabled={loading || !newPassword || newPassword.length < 4}
                className="w-full py-3.5 rounded-2xl bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50 shadow-lg shadow-emerald-200"
                data-testid="reset-password-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Set Password & Login</>}
              </button>
            </form>

          ) : (step === 'otp' || step === 'forgot_otp') ? (
            /* ===== STEP 2: ENTER OTP ===== */
            <div>
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-7 w-7 text-emerald-500" />
              </div>
              <p className="text-center text-sm text-slate-500 mb-1">
                We sent a 6-digit code to
              </p>
              <p className="text-center text-sm font-bold text-[#0A2540] mb-5">{email}</p>

              {/* OTP Input Boxes */}
              <div className="flex justify-center gap-2 mb-4" data-testid="otp-input-group">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onChange={e => handleOTPChange(i, e.target.value)}
                    onKeyDown={e => handleOTPKeyDown(i, e)}
                    className={`w-11 h-12 text-center text-lg font-bold rounded-xl border-2 outline-none transition-all ${
                      digit ? 'border-[#2F80ED] bg-blue-50 text-[#0A2540]' : 'border-slate-200 bg-slate-50 text-slate-700'
                    } focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50`}
                    autoFocus={i === 0}
                    data-testid={`otp-digit-${i}`}
                  />
                ))}
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Loader2 className="h-4 w-4 animate-spin text-[#2F80ED]" />
                  <p className="text-sm text-slate-500">Verifying...</p>
                </div>
              )}

              {/* Dev mode OTP hint */}
              {devOtp && (
                <div className="mb-4 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-center">
                  <p className="text-[10px] text-amber-600 font-semibold">DEV MODE — Your code: <span className="font-mono text-sm text-amber-800">{devOtp}</span></p>
                </div>
              )}

              {/* Resend */}
              <div className="text-center">
                {resendTimer > 0 ? (
                  <p className="text-xs text-slate-400">Resend in <span className="font-bold text-[#2F80ED]">{resendTimer}s</span></p>
                ) : (
                  <button onClick={handleSendOTP} className="text-xs text-[#2F80ED] font-semibold hover:underline" data-testid="otp-resend-btn">
                    Resend Code
                  </button>
                )}
              </div>
            </div>

          ) : (
            /* ===== STEP 3: QUICK PROFILE (unskippable) ===== */
            <form onSubmit={handleProfileSubmit} data-testid="quick-profile-form">
              <div className="w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-3">
                <Sparkles className="h-7 w-7 text-violet-500" />
              </div>
              <p className="text-center text-sm text-slate-500 mb-4">
                Tell us a bit about yourself so we can personalize your experience.
              </p>

              {/* Name */}
              <div className="mb-3">
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={e => setProfileData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Your full name"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                    autoFocus
                    data-testid="quick-profile-name"
                  />
                </div>
              </div>

              {/* Phone Number — Required */}
              <div className="mb-3">
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Mobile Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+61 400 000 000"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                    required
                    data-testid="quick-profile-phone"
                  />
                </div>
              </div>

              {/* Country */}
              <div className="mb-3">
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Current Country</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <select
                    value={profileData.current_country}
                    onChange={e => setProfileData(p => ({ ...p, current_country: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all appearance-none"
                    data-testid="quick-profile-country"
                  >
                    <option value="">Select your country</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* City */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Current City</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={profileData.current_city}
                    onChange={e => setProfileData(p => ({ ...p, current_city: e.target.value }))}
                    placeholder="e.g. Sydney, Toronto, London"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:border-[#2F80ED] focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                    data-testid="quick-profile-city"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-2xl bg-[#2F80ED] text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#1A6BD4] active:scale-[0.98] transition-all disabled:opacity-50 shadow-lg shadow-blue-200"
                data-testid="quick-profile-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" /> Let's Go</>}
              </button>
            </form>
          )}
        </div>

        <style>{`
          @keyframes scaleIn {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-scale-in { animation: scaleIn 0.25s ease-out; }
        `}</style>
      </div>
    </div>
  );
}


function AgentRegisterLink({ onClose }) {
  const navigate = useNavigate();
  return (
    <div className="w-full mt-2 flex flex-col items-center gap-0.5">
      <button type="button"
        onClick={() => { onClose?.(); navigate('/register/agent'); }}
        className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
        data-testid="agent-register-link">
        Are you an Agent? <span className="font-semibold underline">Register here</span>
      </button>
      <button type="button"
        onClick={() => { onClose?.(); navigate('/register/institution'); }}
        className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
        data-testid="institution-register-link">
        Are you an Institution? <span className="font-semibold underline">Register here</span>
      </button>
      {/* Recruiter signup hidden per Feb 14, 2026 spec — keep markup for
          future re-enablement. */}
      {false && (
      <button type="button"
        onClick={() => { onClose?.(); navigate('/recruiter-signup'); }}
        className="text-[10px] text-orange-400 hover:text-orange-600 transition-colors font-medium"
        data-testid="recruiter-register-link">
        Hiring talent? <span className="font-semibold underline">Register as Recruiter</span>
      </button>
      )}
    </div>
  );
}
